import React, { useEffect, useState } from 'react'
import { get } from 'lodash'
import {
	Button, Input, Space, Spin, Table, Tooltip
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import { LeftOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import {
	Cell,
	Legend,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip as RechartsTooltip
} from 'recharts'
import { useDispatch } from 'react-redux'
import { NavLink } from 'react-router-dom'
import slugify from 'slugify'
import { TableHeader } from '../../components/TableHeader'
import { FREQUENCIES } from '../../utils/enums'
import { divideArrayFromAnother } from '../../utils/helper'
import { UsePicker } from '../../components/useFrequenciesPicker'
import { IDashboardJob, IDashboardUser, IDashboardUserDetail } from '../../reducers/dashboard/actions'
import StyledLink from '../../components/NavLink'
import Ratings from '../../components/Ratings'
import CompletedJobsByHourChart from '../../components/statistics/CompletedJobsByHourChart'
import { loadUser } from '../../reducers/user/userActions'
import FinishesPerMonth from '../../components/statistics/FinishesPerMonth'
import queryKeys from '../../queries/queryKeys'
import fetchDashboardWeek from '../../queries/dashboard/fetchDashboardWeek'
import fetchDashboardWorkTime from '../../queries/dashboard/fetchDashboardWorkTime'

const { Search } = Input

interface WeekDay {
	name: string;
	key: string;
}

type TJobsKeys =
	| 'fridayJobs'
	| 'mondayJobs'
	| 'saturdayJobs'
	| 'sundayJobs'
	| 'thursdayJobs'
	| 'tuesdayJobs'
	| 'wednesdayJobs'
	| 'monthJobs'
	| 'quarterYearJobs'
	| 'halfYearJobs'
	| 'yearJobs';

const DAY_IN_MS = 24 * 60 * 60 * 1000
const PIE_COLORS = ['#52c41a', '#ff4d4f']

const getStartOfIsoWeek = (weeksBack: number) => {
	const now = new Date()
	const currentDay = now.getDay() || 7
	const start = new Date(now)

	start.setHours(0, 0, 0, 0)
	start.setDate(now.getDate() - currentDay + 1 - (weeksBack * 7))

	return start
}

const getEndOfIsoWeek = (startOfWeek: Date) => {
	const end = new Date(startOfWeek)

	end.setDate(startOfWeek.getDate() + 6)

	return end
}

const formatDate = (date: Date) => {
	const day = `${date.getDate()}`.padStart(2, '0')
	const month = `${date.getMonth() + 1}`.padStart(2, '0')
	const year = date.getFullYear()

	return `${day}.${month}.${year}`
}

const getIsoWeekNumber = (date: Date) => {
	const target = new Date(date.valueOf())
	const dayNumber = (date.getDay() + 6) % 7

	target.setDate(target.getDate() - dayNumber + 3)

	const firstThursday = new Date(target.getFullYear(), 0, 4)
	const firstThursdayDayNumber = (firstThursday.getDay() + 6) % 7

	firstThursday.setDate(firstThursday.getDate() - firstThursdayDayNumber + 3)

	return 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * DAY_IN_MS))
}

const Dashboard = () => {
	const { t } = useTranslation('pages')
	const dispatch = useDispatch()
	const [weeksBack, setWeeksBack] = useState(0)
	const { filter, Picker } = UsePicker()
	const selectedFrequencies = filter.length > 0 ? filter : FREQUENCIES.map((frequency) => frequency.key)
	const filterOutFrequencies = FREQUENCIES
		.map((frequency) => frequency.key)
		.filter((frequency) => !selectedFrequencies.includes(frequency))
	const {
		data: dashboardData,
		isLoading,
		isFetching: isDashboardFetching,
		refetch: refetchDashboard
	} = useQuery({
		queryKey: queryKeys.dashboard.week({
			filterOutFrequencies,
			weeksBack
		}),
		queryFn: () => fetchDashboardWeek({
			filterOutFrequencies,
			weeksBack
		}),
		staleTime: 1000 * 60,
		cacheTime: 1000 * 60 * 10
	})
	const {
		data: workTimeData,
		isLoading: isWorkTimeLoading,
		isFetching: isWorkTimeFetching,
		refetch: refetchWorkTime
	} = useQuery({
		queryKey: queryKeys.dashboard.workTime(weeksBack),
		queryFn: () => fetchDashboardWorkTime(weeksBack),
		staleTime: 1000 * 60,
		cacheTime: 1000 * 60 * 10
	})
	const startOfWeek = getStartOfIsoWeek(weeksBack)
	const endOfWeek = getEndOfIsoWeek(startOfWeek)
	const weekNumber = getIsoWeekNumber(startOfWeek)
	const weekDate = `${formatDate(startOfWeek)} - ${formatDate(endOfWeek)}`
	const isDashboardWeekSynced = dashboardData?.week?.weeksBack === weeksBack
	const isWorkTimeSynced = workTimeData?.weeksBack === weeksBack
	const data: IDashboardUser[] = isDashboardWeekSynced ? dashboardData?.users || [] : []
	const showRefreshButton = Boolean(isDashboardWeekSynced || isWorkTimeSynced)
	const isRefreshing = isDashboardFetching || isWorkTimeFetching
	const weeklyJobsChartData = isDashboardWeekSynced ? [
		{
			name: t('pages:Dokončené zákazky'),
			value: dashboardData?.weeklyJobsSummary?.completed || 0
		},
		{
			name: t('pages:Nedokončené zákazky'),
			value: dashboardData?.weeklyJobsSummary?.pending || 0
		}
	].filter((item) => item.value > 0) : []

	useEffect(() => {
		dispatch(loadUser())
	}, [dispatch])

	const [searchTerm, setSearchTerm] = useState('')

	const results: IDashboardUser[] = !searchTerm
		? data
		: data?.filter((item: any) => item.user.fullNameSlug.includes(searchTerm))

	// todo filter empty frequencies
	const emptyFrequenciesList: string[] = FREQUENCIES
		.filter((freq) => {
			const jobsKey = (`${freq.key}Jobs`) as TJobsKeys // type-assert

			// Sum up how many jobs exist for this frequency across all users
			const totalJobs = results.reduce((acc, user: IDashboardUser) => {
				const jobsCount = user[jobsKey]?.length || 0
				return acc + jobsCount
			}, 0)

			// Keep this frequency only if totalJobs == 0
			return totalJobs === 0
		})
		.map((freq) => freq.key) || []

	const visibleFrequencies = selectedFrequencies.filter((item: string) => !emptyFrequenciesList.includes(item))
	const filteredWeek = divideArrayFromAnother(FREQUENCIES, visibleFrequencies)
	const createDays = filteredWeek.map((day: WeekDay) => ({
		title: i18next.t(`pages:${day.name}`),
		dataIndex: `${day.key}Jobs`,
		key: day.key,
		render: (value: IDashboardJob[]) => value && value.map((row: IDashboardJob) => <NavLink key={row.id} to={`${i18next.t('paths:jobDetail|path')}/${get(row, 'id')}`}
			style={{ display: 'flex', alignItems: 'start', color: row.isJobDone ? 'green' : 'red' }}>{ get(row, 'address')}</NavLink>)
	}))

	const columns: ColumnsType<IDashboardUser> = [
		{
			title: i18next.t('pages:Meno'),
			dataIndex: 'user',
			key: 'name',
			fixed: true,
			// eslint-disable-next-line react/display-name
			render: (row: IDashboardUserDetail) => <StyledLink to={`${i18next.t('paths:userDetail|path')}/${get(row, 'id')}`}
			>{ `${get(row, 'lastName')} ${get(row, 'firstName')}`}</StyledLink>
		}, ...createDays
	]

	const handleChange = (event: { target: { value: string }; }) => {
		const { value } = event.target
		const slug = slugify(value.toLowerCase(), {
			replacement: '-',
			remove: /[^A-Za-z0-9\s-]/g,
			lower: true
		})
		setSearchTerm(slug)
	}

	const getWeekRangeLabel = (offset: number) => {
		const start = getStartOfIsoWeek(offset)
		const end = getEndOfIsoWeek(start)

		return `${formatDate(start)} - ${formatDate(end)}`
	}

	const handlePreviousWeek = () => {
		setWeeksBack((current) => current + 1)
	}

	const handleNextWeek = () => {
		setWeeksBack((current) => Math.max(0, current - 1))
	}

	const handleCurrentWeek = () => {
		setWeeksBack(0)
	}

	const handleRefresh = async () => {
		await Promise.all([refetchDashboard(), refetchWorkTime()])
	}

	const titleComponent = () => <TableHeader
		left={
			<div style={{
				display: 'flex',
				alignItems: 'center',
				gap: 12,
				flexWrap: 'wrap'
			}}>
				<div>
					<Space size={ 8 } align={ 'center' }>
						<span>{t('pages: Týždeň')}: {weekNumber}</span>
						{weeksBack > 0 ? (
							<Button onClick={handleCurrentWeek}>
								Aktuálny týždeň
							</Button>
						) : null}
						{showRefreshButton ? (
							<Button icon={ <ReloadOutlined /> } loading={ isRefreshing } onClick={ handleRefresh }>
								Refresh
							</Button>
						) : null}
					</Space>
					{isWorkTimeSynced && workTimeData?.allTimeWorkedThisMonthFormatted ? <div className={'font-semibold mt-5'}><h3>Celkový odpracovaný čas za mesiac: {workTimeData.allTimeWorkedThisMonthFormatted}</h3></div> : <div/>}
					<div style={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: 24,
						alignItems: 'flex-start',
						marginTop: 16
					}}>
						{weeklyJobsChartData.length > 0 ? (
							<div style={{ width: 320, height: 220 }}>
								<div className={'font-semibold mb-2'}>
									{t('pages:Týždenné zákazky')}
								</div>
								<ResponsiveContainer width={'100%'} height={'100%'}>
									<PieChart>
										<Pie
											data={weeklyJobsChartData}
											dataKey={'value'}
											nameKey={'name'}
											cx={'50%'}
											cy={'45%'}
											outerRadius={70}
											label
										>
											{weeklyJobsChartData.map((entry, index) => (
												<Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
											))}
										</Pie>
										<RechartsTooltip formatter={(value: number) => `${value}`} />
										<Legend verticalAlign={'bottom'} height={36} />
									</PieChart>
								</ResponsiveContainer>
							</div>
						) : null}
						<CompletedJobsByHourChart weeksBack={weeksBack} />
					</div>
				</div>
			</div>
		}
		right={
			<Space size={ 8 } align={ 'center' }>
				<Tooltip title={ getWeekRangeLabel(weeksBack + 1) }>
					<Button
						icon={ <LeftOutlined /> }
						onClick={ handlePreviousWeek }
					/>
				</Tooltip>
				<span>{weekDate}</span>
				<Tooltip title={ weeksBack > 0 ? getWeekRangeLabel(weeksBack - 1) : undefined }>
					<Button
						icon={ <RightOutlined /> }
						onClick={ handleNextWeek }
						disabled={ weeksBack === 0 }
					/>
				</Tooltip>
			</Space>
		}>
		<Search
			placeholder={t('pages: Vyhľadaj meno')}
			allowClear
			onChange={handleChange}
			style={{ width: 200, marginTop: '50px' }}
		/>
		<Picker/>
	</TableHeader>

	return (
		<div className={'page-wrapper'}>
			<Spin spinning={ isLoading || isWorkTimeLoading }>
				<Table<IDashboardUser>
					key={ weeksBack }
					columns={columns}
					dataSource={results}
					rowKey={(record) => record.user.id}
					bordered
					scroll={{ x: filteredWeek.length > 3 ? 2000 : 0 }}
					rowClassName={'antdTable'}
					title={titleComponent}
					pagination={false}
					// footer={() => "Footer"}
				/>
			</Spin>
			<FinishesPerMonth/>
			<Ratings/>
		</div>
	)
}

Dashboard.propTypes = {}

export default Dashboard
